import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import * as faceapi from "face-api.js";
import { loadModels } from "./modelLoader";
import { recognizeFace } from "./models/faceRecognition.module";

// FIREBASE HOOKS
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { useCollection } from "../firebase-files/hooks/useCollection";

const VisitorFaceScan = forwardRef(
  (
    {
      showStatus = "",
      height = "",
      width = "",
      borderRadius = "",
      handleCamOpenStatus = () => {},
      onMatch = () => {}, // callback if face matched
      onNoMatch = () => {}, // callback if no match
      onError = () => {}, // callback if face detection fails
      setParentStatus = () => {}, // 👈 function to update status in parent
      setShowRegisterFaceBtn = () => {},
    },
    ref
  ) => {
    const { user } = useAuthContext();

    const videoRef = useRef(null);
    const [matched, setMatched] = useState(false);
    const [isScanLine, setScanLine] = useState(false);

    // Fetch users docs
    const { documents: userDocs, error: userDocsError } =
      useCollection("users");

    // Load models + start video
    useEffect(() => {
      const start = async () => {
        await loadModels();
        startVideo();
      };
      start();
    }, []);

    const startVideo = () => {
      navigator.mediaDevices
        .getUserMedia({ video: true })

        .then((stream) => {
          videoRef.current.srcObject = stream;
          console.log("🎥 Camera stream started");

          handleCamOpenStatus();
        })
        .catch((err) => {
          console.error("Webcam error:", err);
        });
    };

    const handleScan = async () => {
      setScanLine(true);
      setParentStatus("🔍 Scanning for face...");

      try {
        const liveDescriptor = await recognizeFace(videoRef.current);

        if (!liveDescriptor || liveDescriptor.length !== 128) {
          const errorSound = new Audio("/assets/sounds/error.mp3");
          errorSound.play();
          onNoMatch();
          setParentStatus("❌ Face not detected");
          setScanLine(false);
          return;
        }

        const validLiveDescriptor = new Float32Array(liveDescriptor);

        let foundMatch = null;

        const allowedRoles = ["admin", "superAdmin", "frontDesk"];
        const isPrivileged = allowedRoles.includes(user?.role);

        if (isPrivileged) {
          // ✅ Compare against all users
          const allUsers = userDocs || [];

          for (const doc of allUsers) {
            const faceList = doc.faceRegistration || [];

            for (const item of faceList) {
              if (
                item &&
                Array.isArray(item.faceDescriptor) &&
                item.faceDescriptor.length === 128
              ) {
                const storedDescriptor = new Float32Array(item.faceDescriptor);
                const distance = faceapi.euclideanDistance(
                  validLiveDescriptor,
                  storedDescriptor
                );

                if (distance < 0.5) {
                  foundMatch = {
                    fullName: doc.fullName || "User",
                    id: doc.id,
                    ...doc,
                  };
                  break;
                }
              }
            }

            if (foundMatch) break;
          }
        } else {
          // ✅ Compare only with logged-in user's face descriptors
          const registeredFaces = (user?.faceRegistration || []).filter(
            (item) =>
              item &&
              Array.isArray(item.faceDescriptor) &&
              item.faceDescriptor.length === 128
          );

          if (registeredFaces.length === 0) {
            const errorSound = new Audio("/assets/sounds/error.mp3");
            errorSound.play();
            setParentStatus("❌ No registered face found");
            setShowRegisterFaceBtn(true);
            setScanLine(false);
            return;
          }

          for (const descriptorData of registeredFaces) {
            const storedDescriptor = new Float32Array(
              descriptorData.faceDescriptor
            );
            const distance = faceapi.euclideanDistance(
              validLiveDescriptor,
              storedDescriptor
            );

            if (distance < 0.5) {
              foundMatch = {
                fullName: user?.fullName || "You",
                id: user?.uid || "",
                ...user,
              };
              break;
            }
          }
        }

        if (foundMatch) {
          const successSound = new Audio("/assets/sounds/success.mp3");
          successSound.play();
          setMatched(true);
          setParentStatus(`✅ Hello ${foundMatch.fullName}`);
          onMatch(foundMatch);
        } else {
          const errorSound = new Audio("/assets/sounds/error.mp3");
          errorSound.play();
          setParentStatus("❌ Face not recognized");
          onNoMatch();
        }

        setScanLine(false);
      } catch (err) {
        const errorSound = new Audio("/assets/sounds/error.mp3");
        errorSound.play();
        setParentStatus("⚠️ Something went wrong");
        onError(err);
        setScanLine(false);
      }
    };

    // const handleScan = async () => {
    //   setScanLine(true);
    //   setParentStatus("🔍 Scanning for face...");

    //   try {
    //     // 1. Get live descriptor from camera
    //     // setParentStatus("📹 Capturing face from camera...");
    //     const liveDescriptor = await recognizeFace(videoRef.current);

    //     if (!liveDescriptor || liveDescriptor.length !== 128) {
    //       const errorSound = new Audio("/assets/sounds/error.mp3");
    //       errorSound.play();
    //       onNoMatch();
    //       setParentStatus("❌ Face not detected");
    //       setScanLine(false);
    //       return;
    //     }

    //     const validLiveDescriptor = new Float32Array(liveDescriptor);

    //     const allowedRoles = ["admin", "superAdmin", "frontDesk"];
    //     const isPrivileged = allowedRoles.includes(user?.role);

    //     // 2. Get current user's stored face descriptors
    //     const registeredFaces = (user?.faceRegistration || []).filter(
    //       (item) =>
    //         item &&
    //         typeof item === "object" &&
    //         Array.isArray(item.faceDescriptor) &&
    //         item.faceDescriptor.length === 128
    //     );

    //     if (registeredFaces.length === 0) {
    //       const errorSound = new Audio("/assets/sounds/error.mp3");
    //       errorSound.play();
    //       setParentStatus("❌ No registered face found");
    //       setShowRegisterFaceBtn(true);
    //       setScanLine(false);
    //       return;
    //     }

    //     let foundMatch = false;

    //     // 3. Compare with each stored descriptor
    //     for (const descriptorData of registeredFaces) {
    //       const storedDescriptor = new Float32Array(
    //         descriptorData.faceDescriptor
    //       );
    //       const distance = faceapi.euclideanDistance(
    //         liveDescriptor,
    //         storedDescriptor
    //       );

    //       if (distance < 0.5) {
    //         foundMatch = true;
    //         break;
    //       }
    //     }

    //     // 4. Decide based on match result
    //     if (foundMatch) {
    //       const successSound = new Audio("/assets/sounds/success.mp3");
    //       successSound.play();
    //       setMatched(true);
    //       setParentStatus(`✅ Hello ${user?.fullName}`);
    //       onMatch(user || "User");
    //     } else {
    //       const errorSound = new Audio("/assets/sounds/error.mp3");
    //       errorSound.play();
    //       setParentStatus("❌ Face not recognized");
    //       onNoMatch();
    //     }

    //     setScanLine(false);
    //   } catch (err) {
    //     const errorSound = new Audio("/assets/sounds/error.mp3");
    //     errorSound.play();
    //     setParentStatus("⚠️ Something went wrong");
    //     onError(err);
    //     setScanLine(false);
    //   }
    // };

    // 👇 expose handleScan to parent
    useImperativeHandle(ref, () => ({
      triggerScan: () => handleScan(),
    }));

    return (
      <div style={{ position: "relative", width: width, height: height }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          style={{
            width: width,
            height: height,
            borderRadius: borderRadius,
            objectFit: "cover",
          }}
        />
        {/* Blue scanning overlay */}
        {isScanLine && (
          <div className="face-scan-overlay">
            <div className="scan-line" />
          </div>
        )}
      </div>
    );
  }
);
export default VisitorFaceScan;
